import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, MailCheck } from 'lucide-react';
import { api, errMsg } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import LangToggle from '@/components/LangToggle';

export default function ForgotPassword() {
  const nav = useNavigate();
  const { t, lang } = useI18n();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      // Identical public response for existing/non-existing emails (no enumeration).
      await api.post('/auth/forgot-password', { email, lang });
    } catch (_) {
      // Even on rate-limit/other errors we show the same generic confirmation.
    } finally {
      setLoading(false);
      setSent(true);
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-asgym-bgdeep">
      <LangToggle className="absolute right-5 top-5 z-20" />
      <div className="relative mx-auto flex min-h-screen w-full max-w-[440px] flex-col justify-center px-6 py-10">
        <div
          className="rounded-3xl border border-white/[0.06] bg-[#16171A]/80 p-6 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.8)] backdrop-blur-xl animate-fade-up"
          data-testid="forgot-card"
        >
          {sent ? (
            <div className="text-center" data-testid="forgot-success">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-asgym-accent/15">
                <MailCheck className="text-asgym-accent" size={26} />
              </div>
              <h2 className="font-display text-2xl font-extrabold tracking-tight text-white">{t('forgot.sentTitle')}</h2>
              <p className="mt-3 text-[13px] leading-relaxed text-asgym-sub">{t('forgot.success')}</p>
              <button
                type="button"
                data-testid="goto-login"
                onClick={() => nav('/')}
                className="as-btn-primary mt-8 w-full"
              >
                {t('forgot.backToLogin')}
              </button>
            </div>
          ) : (
            <form onSubmit={submit}>
              <button
                type="button"
                data-testid="forgot-back"
                onClick={() => nav('/')}
                className="mb-4 flex items-center gap-1.5 text-[13px] text-asgym-sub hover:text-white"
              >
                <ArrowLeft size={16} /> {t('forgot.backToLogin')}
              </button>
              <h2 className="font-display text-2xl font-extrabold tracking-tight text-white">{t('forgot.title')}</h2>
              <p className="mb-6 mt-1.5 text-[13px] text-asgym-sub">{t('forgot.sub')}</p>

              <label className="as-label">{t('forgot.email')}</label>
              <input
                data-testid="forgot-email"
                className="as-input mt-2"
                type="email"
                autoCapitalize="none"
                placeholder={t('login.placeholderEmail')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />

              <button type="submit" data-testid="forgot-submit" disabled={loading} className="as-btn-primary mt-8 w-full">
                {loading ? <Loader2 className="animate-spin" size={20} /> : t('forgot.submit')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
